import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Award } from "lucide-react";
import { cn } from "@nexus/ui";
import { avatarColor } from "@/lib/avatar-color";
import { canAnimate } from "@/lib/motion";
import {
  buildBenchmarkDelta,
  formatBrandName,
  formatCountValue,
} from "../../lib/format";

export interface CompetitorKpiCardProps {
  title: string;
  you: number;
  competitor: number;
  benchmark: number;
  youLabel?: string;
  competitorLabel?: string;
  isLoading?: boolean;
  skeletonDelayMs?: number;
  animationKey?: string;
  isSelected?: boolean;
  isClickable?: boolean;
  onClick?: () => void;
}

const ANIMATION_MS = 900;

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function useCountUp(
  target: number,
  duration: number,
  enabled: boolean,
  animationKey?: string,
): number {
  const [value, setValue] = useState(() => (enabled && !canAnimate() ? target : 0));
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      setValue(0);
      return;
    }

    if (!canAnimate()) {
      setValue(target);
      return;
    }

    let cancelled = false;
    setValue(0);
    const start = performance.now();

    const tick = (now: number) => {
      if (cancelled) return;
      const progress = Math.min((now - start) / duration, 1);
      setValue(target * easeOutCubic(progress));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [target, duration, enabled, animationKey]);

  return value;
}

function competitorInitial(label: string): string {
  const formatted = formatBrandName(label);
  return formatted.charAt(0).toUpperCase() || "?";
}

export function CompetitorKpiCard({
  title,
  you,
  competitor,
  benchmark,
  youLabel,
  competitorLabel,
  isLoading = false,
  skeletonDelayMs = 0,
  animationKey,
  isSelected = false,
  isClickable = false,
  onClick,
}: CompetitorKpiCardProps) {
  const { t } = useTranslation();
  const ready = !isLoading;
  const animatedYou = useCountUp(you, ANIMATION_MS, ready, animationKey);
  const delta = buildBenchmarkDelta(you, benchmark);

  const resolvedYouLabel = formatBrandName(youLabel ?? t("dashboard.kpiCards.you"));
  const resolvedCompetitorLabel = formatBrandName(
    competitorLabel ?? t("dashboard.kpiCards.competitor"),
  );
  const competitorAvatarColor = avatarColor(resolvedCompetitorLabel);

  if (isLoading) {
    return (
      <div
        className="flex flex-col gap-3 rounded-xl border bg-card p-4"
        style={{ "--shimmer-delay": `${skeletonDelayMs}ms` } as React.CSSProperties}
        aria-busy
      >
        <div className="skeleton-shimmer-bar h-3.5 w-24 rounded-md" />
        <div className="flex items-center gap-2">
          <div className="skeleton-shimmer-bar h-7 w-16 rounded-md" />
          <div className="skeleton-shimmer-bar h-6 w-28 rounded-full" />
        </div>
        <div className="skeleton-shimmer-bar mt-auto h-4 w-full max-w-[12rem] rounded-md" />
      </div>
    );
  }

  const displayYou = formatCountValue(animatedYou);
  const headlineAria = t("dashboard.kpiCards.yourValueAria", {
    brand: resolvedYouLabel,
    value: formatCountValue(you),
  });

  function handleKeyDown(event: React.KeyboardEvent) {
    if (!isClickable || !onClick) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  }

  return (
    <div
      className={cn(
        "flex h-full flex-col rounded-xl border bg-card p-4",
        isClickable && "cursor-pointer transition-shadow hover:ring-2 hover:ring-ring/40",
        isSelected && "ring-2 ring-primary",
      )}
      data-competitor-kpi-card
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
    >
      <p className="pb-3 text-[13px] leading-[19.5px] tracking-[-0.0762px] text-foreground">
        {title}
      </p>

      <div className="flex flex-wrap items-center gap-2 pb-3">
        <span
          className="text-[22px] font-semibold leading-[22px] tracking-[-0.2578px] tabular-nums text-foreground"
          aria-label={headlineAria}
        >
          {displayYou}
        </span>

        {competitor > 0 && (
          <div className="inline-flex items-center gap-1 rounded-full border bg-muted px-2 py-0.5">
            <span className="text-[11px] leading-[16.5px] tracking-[0.0645px] text-muted-foreground">
              {t("dashboard.kpiCards.vs")}
            </span>
            <span
              className="flex size-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold uppercase text-white"
              style={{ backgroundColor: competitorAvatarColor }}
              aria-hidden
            >
              {competitorInitial(resolvedCompetitorLabel)}
            </span>
            <span className="text-xs tabular-nums text-foreground">
              {formatCountValue(competitor)}
            </span>
          </div>
        )}
      </div>

      <div className="mt-auto flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs leading-[18px]">
        {delta.hasBenchmark ? (
          <>
            <span
              className={cn(
                "inline-flex items-center gap-1 font-medium tabular-nums",
                delta.isPositive
                  ? "text-emerald-700 dark:text-emerald-400"
                  : "text-destructive",
              )}
            >
              {delta.isPositive ? (
                <ArrowUp className="size-3.5 shrink-0" aria-hidden />
              ) : (
                <ArrowDown className="size-3.5 shrink-0" aria-hidden />
              )}
              {delta.isPositive ? "+" : ""}
              {Math.round(delta.percent)}%
            </span>
            <span className="text-muted-foreground">
              {t("dashboard.kpiCards.vsBenchmark", {
                value: formatCountValue(benchmark),
              })}
            </span>
            <Award className="size-3 shrink-0 text-muted-foreground" aria-hidden />
          </>
        ) : (
          <span className="text-muted-foreground">{t("dashboard.kpiCards.noBenchmark")}</span>
        )}
      </div>
    </div>
  );
}
